import { Badge } from '@lib/components'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { CONNECT_FIGMA, DISCONNECT_FIGMA } from '@storeOld/actions'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import API from '@/api'
import { isAdvancedPlan } from '@/constants/userSubscriptionPlans'
import { useProjectAccess } from '@/hooks'
import { FIGMA_PREVIEWS_PERMISSION } from '@/permissions/project/definitions'
import type { AppState } from '@/types'
import { MODAL_UPGRADE_PLAN } from '../constants'
import useFigma from './hooks/useFigma'

import './styles.scss'

type Props = {
  closeButton: React.ReactNode
}

interface LinkIntegration {
  connectUrl: string
  description: string
  iconPath: string
  name: string
}

// Connect у них ведёт на страницу интеграции, а не поднимает OAuth: сам
// Google Analytics подключается из SEO-режима, в проекте.
const INTEGRATIONS: LinkIntegration[] = [
  {
    connectUrl: 'https://claude.ai/directory/octopus-do',
    description: 'Build and edit sitemaps with Claude',
    iconPath: '/assets/images/integrations/claude.svg',
    name: 'Claude',
  },
  {
    connectUrl:
      'https://chatgpt.com/plugins/plugin_asdk_app_6a5e27365db881919a72170f6ddb019b',
    description: 'Build and edit sitemaps with ChatGPT',
    iconPath: '/assets/images/integrations/chatgpt.svg',
    name: 'ChatGPT',
  },
  {
    connectUrl: 'https://octopus.do/docs/apps/google-analytics',
    description: 'See page views and engagement in SEO mode',
    iconPath: '/assets/images/integrations/google-analytics.svg',
    name: 'Google Analytics',
  },
]

const formatDate = (instance: any) => {
  const date = new Date(instance)
  const day = date.getDate()
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const month = months[date.getMonth()]
  const year = date.getFullYear()

  return `${day} ${month} ${year}`
}

const Integrations: React.FC<Props> = ({ closeButton }) => {
  const { hasAccess } = useProjectAccess(FIGMA_PREVIEWS_PERMISSION.SET_UP)
  const { connect: connectViaFigma, authorize } = useFigma()
  const [opener, setOpener] = useState<any>(null)
  const { figma } = useSelector(
    (state: Omit<AppState, 'project' | 'tree'>) => ({
      figma: state.auth.integrations.figma,
    })
  )
  const projectId = useSelector(migrationProjectSelector.getProjectId)
  const userPlanId = useSelector<AppState, string>((state) => state.auth.planId)
  const dispatch = useDispatch()

  const onBadgeClick = () => {
    dispatch(openModal(MODAL_UPGRADE_PLAN))
  }

  let hasToFigmaAccess = false
  if (!projectId && userPlanId) {
    hasToFigmaAccess = isAdvancedPlan(userPlanId)
  } else {
    hasToFigmaAccess = hasAccess
  }

  useEffect(() => {
    if (!window.opener) {
      window.onmessage = ({ data: { type, data } }: any) => {
        authorize({
          type,
          data,
        })?.then((res) => {
          if (opener) {
            if (res.error) {
              opener.postMessage({ isSuccess: false }, window.origin)
            } else {
              opener.postMessage({ isSuccess: true }, window.origin)

              dispatch({
                type: CONNECT_FIGMA,
                payload: new Date().getTime(),
              })
            }
          }
        })
      }
    }
  }, [opener])

  return (
    <div className="integrations-modal">
      <div className="integrations-modal-header">
        <div className="integrations-modal-heading">Integrations</div>

        {closeButton}
      </div>

      <div className="integrations-modal-list">
        {hasToFigmaAccess ? (
          <div className="integration">
            <div className="integration-information">
              <div className="integration-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_2755_120391)">
                    <path
                      d="M8.49994 22.4999C10.4319 22.4999 11.9999 20.932 11.9999 19V15.5H8.49994C6.56794 15.5 4.99994 17.068 4.99994 19C4.99994 20.932 6.56794 22.4999 8.49994 22.4999Z"
                      fill="#0ACF83"
                    />
                    <path
                      d="M4.99994 12C4.99994 10.068 6.56794 8.5 8.49994 8.5H11.9999V15.4999H8.49994C6.56794 15.4999 4.99994 13.932 4.99994 12Z"
                      fill="#A259FF"
                    />
                    <path
                      d="M5.00012 4.99999C5.00012 3.068 6.56812 1.5 8.50013 1.5H12.0001V8.49999H8.50013C6.56812 8.49999 5.00012 6.93199 5.00012 4.99999Z"
                      fill="#F24E1E"
                    />
                    <path
                      d="M11.9999 1.5H15.5C17.432 1.5 19 3.068 19 4.99999C19 6.93199 17.432 8.49999 15.5 8.49999H11.9999V1.5Z"
                      fill="#FF7262"
                    />
                    <path
                      d="M19 12C19 13.932 17.432 15.4999 15.5 15.4999C13.568 15.4999 11.9999 13.932 11.9999 12C11.9999 10.068 13.568 8.5 15.5 8.5C17.432 8.5 19 10.068 19 12Z"
                      fill="#1ABCFE"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2755_120391">
                      <rect
                        width="14.0028"
                        height="21"
                        fill="white"
                        transform="translate(4.99854 1.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </div>

              <div>
                <div className="integration-product">Figma</div>

                <div className="integration-description">
                  Import Figma artboards as page covers
                </div>

                {figma && (
                  <div className="integration-connected-date">
                    <svg
                      width="14"
                      height="11"
                      viewBox="0 0 14 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.2734 0.306641C12.332 0.248047 12.3906 0.21875 12.5078 0.21875C12.5957 0.21875 12.6836 0.248047 12.7715 0.306641L13.5918 1.15625C13.6504 1.21484 13.709 1.30273 13.709 1.39062C13.709 1.50781 13.6504 1.5957 13.5918 1.6543L4.80273 10.4434C4.74414 10.502 4.65625 10.5312 4.56836 10.5312C4.45117 10.5312 4.36328 10.502 4.30469 10.4434L0.408203 6.51758C0.320312 6.45898 0.291016 6.40039 0.291016 6.2832C0.291016 6.19531 0.320312 6.10742 0.408203 6.01953L1.22852 5.19922C1.28711 5.14062 1.375 5.08203 1.49219 5.08203C1.58008 5.08203 1.66797 5.14062 1.72656 5.19922L4.56836 8.04102L12.2734 0.306641Z"
                        fill="#18C4A9"
                      />
                    </svg>
                    Connected from {formatDate(figma)}
                  </div>
                )}
              </div>
            </div>

            {!figma ? (
              <button
                className="integration-button"
                onClick={() => {
                  const window = connectViaFigma()

                  setOpener(window)
                }}
              >
                Connect
              </button>
            ) : (
              <button
                className="integration-button connected"
                onClick={async () => {
                  await API.Auth.disconnect()

                  dispatch({
                    type: DISCONNECT_FIGMA,
                  })
                }}
              >
                Disconnect
              </button>
            )}
          </div>
        ) : (
          <div className="integration soon">
            <div className="integration-information">
              <div className="integration-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_2755_120391)">
                    <path
                      d="M8.49994 22.4999C10.4319 22.4999 11.9999 20.932 11.9999 19V15.5H8.49994C6.56794 15.5 4.99994 17.068 4.99994 19C4.99994 20.932 6.56794 22.4999 8.49994 22.4999Z"
                      fill="#0ACF83"
                    />
                    <path
                      d="M4.99994 12C4.99994 10.068 6.56794 8.5 8.49994 8.5H11.9999V15.4999H8.49994C6.56794 15.4999 4.99994 13.932 4.99994 12Z"
                      fill="#A259FF"
                    />
                    <path
                      d="M5.00012 4.99999C5.00012 3.068 6.56812 1.5 8.50013 1.5H12.0001V8.49999H8.50013C6.56812 8.49999 5.00012 6.93199 5.00012 4.99999Z"
                      fill="#F24E1E"
                    />
                    <path
                      d="M11.9999 1.5H15.5C17.432 1.5 19 3.068 19 4.99999C19 6.93199 17.432 8.49999 15.5 8.49999H11.9999V1.5Z"
                      fill="#FF7262"
                    />
                    <path
                      d="M19 12C19 13.932 17.432 15.4999 15.5 15.4999C13.568 15.4999 11.9999 13.932 11.9999 12C11.9999 10.068 13.568 8.5 15.5 8.5C17.432 8.5 19 10.068 19 12Z"
                      fill="#1ABCFE"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2755_120391">
                      <rect
                        width="14.0028"
                        height="21"
                        fill="white"
                        transform="translate(4.99854 1.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </div>

              <div>
                <div className="integration-product">
                  <span>Figma</span> <Badge type="pro" onClick={onBadgeClick} />
                </div>

                <div className="integration-description">
                  Import Figma artboards as page covers
                </div>
              </div>
            </div>
          </div>
        )}

        {INTEGRATIONS.map(({ connectUrl, description, iconPath, name }) => (
          <div className="integration" key={name}>
            <div className="integration-information">
              <div className="integration-icon integration-link-icon">
                <img alt={`${name} logo`} src={iconPath} />
              </div>

              <div>
                <div className="integration-product">{name}</div>
                <div className="integration-description">{description}</div>
              </div>
            </div>

            <a
              className="integration-button"
              href={connectUrl}
              rel="noreferrer"
              target="_blank"
            >
              Connect
            </a>
          </div>
        ))}
      </div>
    </div>
  )
}

export default Integrations
